'use client'; // This component uses hooks

import React from 'react'; // Removed useState as it's no longer needed for ContactFormDialog
import Link from 'next/link';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
// Removed unused icons: Zap, PenTool, Clock, Download, MessageCircle, Github, Twitter, Linkedin, ArrowRight

import dynamic from 'next/dynamic';
import { appConfig } from '@/config/appConfig';

// Removed imports for LoadOnVisible, SubscriptionPlansSkeleton, ContactFormDialog
// as these sections are being removed.
import PageLoader from '@/components/common/PageLoader';

// Dynamic import for AuroraBackground (unchanged)
const AuroraBackground = dynamic(
    () =>
        import('@/components/ui/aurora-background').then(
            (mod) => mod.AuroraBackground
        ),
    {
        loading: () => (
            <div className="relative flex h-[100vh] flex-col items-center justify-center bg-zinc-50 text-slate-950 dark:bg-zinc-900">
                <PageLoader />
            </div>
        ),
        ssr: false,
    }
);

export default function LandingPageClient() {
    const {
        t,
        isLoading: isTranslationLoading,
        currentLanguage,
    } = useTranslation();
    // Removed isContactOpen and setIsContactOpen as ContactFormDialog is removed

    if (isTranslationLoading) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <PageLoader />
            </div>
        );
    }

    return (
        <div className="flex min-h-screen flex-col bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-800 dark:via-gray-900 dark:to-purple-900/30">
            <main className="flex-1">
                {/* Hero Section - Simplified */}
                <section className="w-full">
                    <AuroraBackground>
                        <div className="container px-4 md:px-6 mx-auto max-w-5xl relative z-10">
                            <div className="flex flex-col items-center space-y-6 text-center">
                                <h1 className="text-4xl font-bold tracking-tighter sm:text-5xl md:text-6xl lg:text-7xl/none text-foreground">
                                    {t('landing.hero.title', {
                                        fallback: 'Welcome to CigarSnap',
                                    })}
                                </h1>
                                <div className="space-x-4 pt-4">
                                    <Button asChild size="lg" className="h-11">
                                        <Link
                                            href={`/${currentLanguage}/login`} // Link to Login page
                                            className="flex items-center"
                                        >
                                            {t('common.login', {
                                                fallback: 'Login',
                                            })}
                                        </Link>
                                    </Button>
                                    <Button
                                        asChild
                                        variant="outline"
                                        size="lg"
                                        className="h-11 text-foreground"
                                    >
                                        <Link
                                            href={`/${currentLanguage}/signup`} // Link to Sign Up page
                                            className="flex items-center"
                                        >
                                            {t('common.signup', {
                                                fallback: 'Sign Up',
                                            })}
                                        </Link>
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </AuroraBackground>
                </section>

                {/* REMOVED: Features Section */}
                {/* REMOVED: Pricing Section Preview */}
                {/* REMOVED: Contact Section */}
                {/* REMOVED: Powered by xAI Section */}
            </main>
            {/* Footer - Kept */}
            <footer className="border-t">
                <div className="container mx-auto max-w-5xl flex flex-col items-center justify-center gap-4 py-10 md:h-24 md:flex-row md:py-0 px-4 md:px-6">
                    <p className="text-center text-sm text-muted-foreground flex-1">
                        {t('landing.footer.copy', {
                            year: new Date().getFullYear().toString(),
                            fallback: `© ${new Date().getFullYear()} CigarSnap. All rights reserved.`,
                        })}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <Link
                            href={`/${currentLanguage}/privacy`}
                            className="hover:text-foreground"
                        >
                            {t('landing.footer.privacy', {
                                fallback: 'Privacy Policy',
                            })}
                        </Link>
                        <Link
                            href={`/${currentLanguage}/terms`}
                            className="hover:text-foreground"
                        >
                            {t('landing.footer.terms', {
                                fallback: 'Terms of Service',
                            })}
                        </Link>
                        {appConfig.externalLinks.twitter && (
                            <Link
                                href={appConfig.externalLinks.twitter}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:text-foreground"
                                aria-label="Twitter"
                            >
                                {/* Icon removed, simple text link or use Lucide icon component if preferred */}
                                Twitter
                            </Link>
                        )}
                    </div>
                </div>
            </footer>
            {/* REMOVED: Contact Dialog */}
        </div>
    );
}
